import type {
  ClientPost,
  PagedModelClientPost
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';

const getGroupIdForPost = (post: ClientPost): string | undefined =>
  post.gossip?.groupId ||
  post.happening?.groupId ||
  post.newsItem?.groupId ||
  post.offer?.groupId ||
  post.seeking?.groupId ||
  post.specialPost?.groupId ||
  post.suggestion?.groupId;

/**
 * Should be used when a person associated with posts has changed (e.g., blocked)
 */
export const invalidatePostCachesForPerson = (
  queryClient: QueryClient,
  // oxlint-disable-next-line @typescript-eslint/no-unused-vars
  _personId: string
) => {
  // TODO: Optimize. Invalidate only queries related to the person

  // Posts
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.POST_FEED] });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.POST_FEED_LIMITED]
  });
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.POST_SEARCH] });
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.OWN_POSTS] });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.OWN_POSTS_WITH_ACTIVITY]
  });
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.GROUP_POSTS] });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MEIN_ORT_POST_FEED]
  });
  // Comments
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.COMMENTS] });
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.COMMENT] });
};

export const invalidatePostCachesForHomeGeoArea = (
  queryClient: QueryClient
): void => {
  // API_CACHE_KEYS.POST are unaffected
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.POST_FEED] });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.POST_FEED_LIMITED]
  });
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.POST_SEARCH] });
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.OWN_POSTS] });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.OWN_POSTS_WITH_ACTIVITY]
  });
  // API_CACHE_KEYS.GROUP_POSTS are unaffected
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MEIN_ORT_POST_FEED]
  });

  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MINISTRY_POST_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.REGIONALFUNK_POST_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MY_REGION_EVENTS_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.EMERGENCY_POST_FEED]
  });

  // API_CACHE_KEYS.COMMENTS are unaffected
  // API_CACHE_KEYS.COMMENT are unaffected
};

export const invalidatePostCachesForNewPost = (
  queryClient: QueryClient,
  newPost: ClientPost
): void => {
  // API_CACHE_KEYS.POST are up-to date
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.POST_FEED] });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.POST_FEED_LIMITED]
  });
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.POST_SEARCH] });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.POSTS_BY_GEO_AREA]
  });
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.OWN_POSTS] });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.OWN_POSTS_WITH_ACTIVITY]
  });
  const groupId = getGroupIdForPost(newPost);
  if (groupId) {
    queryClient.invalidateQueries({
      queryKey: [API_CACHE_KEYS.GROUP_POSTS, groupId]
    });
  }
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MEIN_ORT_POST_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.SAGS_UNS_POST_FEED]
  });

  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.ORGANIZATION_POSTS]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.ORGANIZATION_POSTS_WITH_ACTIVITIES]
  });

  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MINISTRY_POST_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.REGIONALFUNK_POST_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MY_REGION_EVENTS_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.EMERGENCY_POST_FEED]
  });

  // API_CACHE_KEYS.COMMENTS are up-to date
  // API_CACHE_KEYS.COMMENT are up-to date
};

export const invalidatePostCachesForChangedPost = (
  queryClient: QueryClient,
  changedPost: ClientPost
): void => {
  const postId = changedPost.id;

  queryClient.setQueryData([API_CACHE_KEYS.POST, { postId }], changedPost);

  queryClient.setQueriesData(
    { queryKey: [API_CACHE_KEYS.POST_FEED] },
    (feed: InfiniteData<PagedModelClientPost, number>) => {
      if (!feed) return feed;
      return {
        ...feed,
        pages: feed.pages.map((feedPage: PagedModelClientPost) => ({
          ...feedPage,
          content: feedPage.content?.map((p) =>
            p.id === postId ? changedPost : p
          )
        }))
      };
    }
  );

  queryClient.setQueriesData(
    { queryKey: [API_CACHE_KEYS.POST_FEED_LIMITED] },
    (page: PagedModelClientPost) => {
      if (!page) return page;
      return {
        ...page,
        content: page.content?.map((p) => (p.id === postId ? changedPost : p))
      };
    }
  );

  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.POST_SEARCH] });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.POSTS_BY_GEO_AREA]
  });

  queryClient.setQueriesData(
    { queryKey: [API_CACHE_KEYS.OWN_POSTS] },
    (feed: InfiniteData<PagedModelClientPost, number>) => {
      if (!feed) return feed;
      return {
        ...feed,
        pages: feed.pages.map((feedPage: PagedModelClientPost) => ({
          ...feedPage,
          content: feedPage.content?.map((p) =>
            p.id === postId ? changedPost : p
          )
        }))
      };
    }
  );

  queryClient.setQueriesData(
    { queryKey: [API_CACHE_KEYS.OWN_POSTS_WITH_ACTIVITY] },
    (feed: InfiniteData<PagedModelClientPost, number>) => {
      if (!feed) return feed;
      return {
        ...feed,
        pages: feed.pages.map((feedPage: PagedModelClientPost) => ({
          ...feedPage,
          content: feedPage.content?.map((p) =>
            p.id === postId ? changedPost : p
          )
        }))
      };
    }
  );

  const groupId = getGroupIdForPost(changedPost);
  if (groupId) {
    queryClient.invalidateQueries({
      queryKey: [API_CACHE_KEYS.GROUP_POSTS, groupId]
    });
  }

  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MEIN_ORT_POST_FEED]
  });

  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.ORGANIZATION_POSTS]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.ORGANIZATION_POSTS_WITH_ACTIVITIES]
  });

  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MINISTRY_POST_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.REGIONALFUNK_POST_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MY_REGION_EVENTS_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.EMERGENCY_POST_FEED]
  });

  // API_CACHE_KEYS.COMMENTS are unaffected
  // API_CACHE_KEYS.COMMENT are unaffected
};

export const invalidatePostCachesForDeletedPost = (
  queryClient: QueryClient,
  deletedPostId: string
): void => {
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.POST, { postId: deletedPostId }]
  });

  queryClient.setQueriesData(
    { queryKey: [API_CACHE_KEYS.POST_FEED] },
    (feed: InfiniteData<PagedModelClientPost, number>) => {
      if (!feed) return feed;
      return {
        ...feed,
        pages: feed.pages.map((feedPage: PagedModelClientPost) => ({
          ...feedPage,
          content: feedPage.content?.filter((post) => post.id !== deletedPostId)
        }))
      };
    }
  );

  queryClient.setQueriesData(
    { queryKey: [API_CACHE_KEYS.POST_FEED_LIMITED] },
    (page: PagedModelClientPost) => {
      if (!page) return page;
      return {
        ...page,
        content: page.content?.filter((post) => post.id !== deletedPostId)
      };
    }
  );

  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.POST_SEARCH] });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.POSTS_BY_GEO_AREA]
  });

  queryClient.setQueriesData(
    { queryKey: [API_CACHE_KEYS.OWN_POSTS] },
    (feed: InfiniteData<PagedModelClientPost, number>) => {
      if (!feed) return feed;
      return {
        ...feed,
        pages: feed.pages.map((feedPage: PagedModelClientPost) => ({
          ...feedPage,
          content: feedPage.content?.filter((post) => post.id !== deletedPostId)
        }))
      };
    }
  );

  queryClient.setQueriesData(
    { queryKey: [API_CACHE_KEYS.OWN_POSTS_WITH_ACTIVITY] },
    (feed: InfiniteData<PagedModelClientPost, number>) => {
      if (!feed) return feed;
      return {
        ...feed,
        pages: feed.pages.map((feedPage: PagedModelClientPost) => ({
          ...feedPage,
          content: feedPage.content?.filter((post) => post.id !== deletedPostId)
        }))
      };
    }
  );

  // TODO: Optimize. Invalidate only queries with the specified post id
  queryClient.invalidateQueries({ queryKey: [API_CACHE_KEYS.GROUP_POSTS] });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MEIN_ORT_POST_FEED]
  });

  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.ORGANIZATION_POSTS]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.ORGANIZATION_POSTS_WITH_ACTIVITIES]
  });

  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MINISTRY_POST_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.REGIONALFUNK_POST_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.MY_REGION_EVENTS_FEED]
  });
  queryClient.invalidateQueries({
    queryKey: [API_CACHE_KEYS.EMERGENCY_POST_FEED]
  });

  // API_CACHE_KEYS.COMMENTS are unaffected
  // API_CACHE_KEYS.COMMENT are unaffected
};
